import { useMatch, useSearchParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { FallbackProps } from 'react-error-boundary';
import { core_v1_DataSource } from '@antimatterhq/asl-api-ts-v1';
import { Skeleton } from '@antimatterhq/ui/components/skeleton';
import Spacer from '@antimatterhq/ui/components/spacer';
import { GlobalError } from '@antimatterhq/ui/components/globalError';
import ConfigureDataSource from '../configure';
import { initDataSource } from '@/lib/datasource';
import {
  ConfigDataSourceProvider,
  StepId,
} from '@/contexts/ConfigDataSourceContext';
import { useGetDatasourcePresetWithSuspense } from '@/hooks/content/useGetDatasourcePreset';
import { SuspenseErrorBoundary } from '@/components/errorBoundary/SuspenseErrorBoundary';

function AddDataSourceSuspense({
  usePreset,
  datasource,
  step,
  usePresetVersion,
}: {
  usePreset: string;
  datasource: core_v1_DataSource;
  step: StepId;
  usePresetVersion?: number;
}) {
  const { data: preset } = useGetDatasourcePresetWithSuspense({
    name: usePreset,
    version: usePresetVersion,
  });

  return (
    <ConfigDataSourceProvider
      dataSource={datasource}
      currentStep={step}
      isEdit={false}
      preset={preset}
      data-testid="add-datasource-provider"
    >
      <div data-testid="add-datasource-page">
        <ConfigureDataSource />
      </div>
    </ConfigDataSourceProvider>
  );
}

const AddDataSourceErrorBoundaryFallback = ({
  error,
  resetErrorBoundary,
}: FallbackProps) => {
  let errorMessage = isAxiosError(error)
    ? (error.response?.statusText ?? error.message)
    : error.message;

  if (isAxiosError(error) && error.response?.status === 404) {
    errorMessage = `Preset "${error.response?.data.identifier}" not found`;
  }

  return (
    <GlobalError
      title="Error creating datasource"
      description={errorMessage}
      onRetry={async () => resetErrorBoundary()}
      retryText="Retry"
      className="h-[75vh]"
      data-testid="datasource-creation-error"
    />
  );
};

function AddDataSourceStepSuspense() {
  const dataSource = initDataSource();
  const match = useMatch('/configure/data-sources/add/:step');
  const step = match?.params?.step as StepId;
  const [searchParams] = useSearchParams();

  const source = searchParams.get('source');
  const sourceType = searchParams.get('sourceType');
  const usePreset = searchParams.get('usePreset');
  const usePresetVersion = searchParams.get('usePresetVersion');

  if (source && sourceType && usePreset) {
    dataSource.spec.source = source;
    dataSource.spec.sourceType = sourceType;
    dataSource.spec.usePreset = usePreset;
    if (usePresetVersion) {
      dataSource.spec.usePresetVersion = Number(usePresetVersion);
    }
  }

  if (usePreset) {
    return (
      <AddDataSourceSuspense
        usePreset={usePreset}
        datasource={dataSource}
        step={step}
        usePresetVersion={
          usePresetVersion ? Number(usePresetVersion) : undefined
        }
      />
    );
  }

  return (
    <ConfigDataSourceProvider
      dataSource={dataSource}
      currentStep={step}
      isEdit={false}
      data-testid="add-datasource-provider"
    >
      <div data-testid="add-datasource-page">
        <ConfigureDataSource />
      </div>
    </ConfigDataSourceProvider>
  );
}

export default function AddDataSourceStep() {
  return (
    <SuspenseErrorBoundary
      suspenseFallback={<AddDataSourceSuspenseFallback />}
      errorBoundaryFallback={AddDataSourceErrorBoundaryFallback}
      data-testid="add-datasource-error-boundary"
    >
      <AddDataSourceStepSuspense />
    </SuspenseErrorBoundary>
  );
}

function AddDataSourceSuspenseFallback() {
  return (
    <div
      className="flex h-full w-full flex-col p-4"
      data-testid="add-datasource-loading"
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4">
          <Skeleton
            className="h-14 w-56"
            data-testid="add-datasource-title-skeleton"
          />
          <Skeleton
            className="h-10 w-full max-w-[850px]"
            data-testid="add-datasource-steps-skeleton"
          />
        </div>
        <Spacer size="sm" />
        <div
          className="flex max-w-[600px] flex-col gap-2"
          data-testid="add-datasource-form-skeleton"
        >
          <Skeleton
            className="h-6 w-20"
            data-testid="add-datasource-form-field-skeleton-1"
          />
          <Skeleton className="h-10 w-full" />

          <Skeleton
            className="h-6 w-20"
            data-testid="add-datasource-form-field-skeleton-2"
          />
          <Skeleton className="h-28 w-full" />

          <Skeleton
            className="h-6 w-20"
            data-testid="add-datasource-form-field-skeleton-3"
          />
          <Skeleton className="h-10 w-full" />

          <Skeleton
            className="h-6 w-20"
            data-testid="add-datasource-form-field-skeleton-4"
          />
          <Skeleton className="h-10 w-20" />

          <Skeleton
            className="h-6 w-20"
            data-testid="add-datasource-form-field-skeleton-5"
          />
          <Skeleton className="h-10 w-full" />

          <Skeleton
            className="h-6 w-20"
            data-testid="add-datasource-form-field-skeleton-6"
          />
          <div
            className="flex gap-4"
            data-testid="add-datasource-buttons-skeleton"
          >
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      </div>
    </div>
  );
}
